import { useEffect, useMemo, useState } from 'react';
import {
  getChildTenants,
  getCurrentTenant,
  getOrganizationProfile,
  getOrganizations
} from 'redux/slices/organization';
import { RootState, useSelector } from 'redux/store';
import { Avatar } from '@mui/material';
import { CustomDataGrid } from 'components/datagrid/Custom';
import { GridColDef } from '@mui/x-data-grid';
import useCustomDispatch from 'redux/dispatch';
import { DataGridKey } from 'redux/slices/datagrid';
import { ITranslator } from 'translation/generated/translationInterface';
import { useTranslator } from 'translation/useTranslator';
import EditOrganizationDialog from './EditOrganizationDialog';
import { editColumnDefinition } from 'components/datagrid/columns/EditColumnDefinition';
import { EditListItem } from 'components/datagrid/listItems/EditListItem';
import { activeColumnDefinition } from 'components/datagrid/columns/ActiveColumnDefinition';
import { fDate } from '@nps.today/component-library';
import AccessGuard, { rules, useAccess } from 'access';
import ChildTenantAccessCell from './ChildTenantAccessCell';
import ChildTenantAccessPanel from './ChildTenantAccessPanel';
import { ChildTenant } from '../../../@types/organization';

let thumbSize = 48;
const Organizations = () => {
  const columns = (translator: ITranslator): GridColDef[] => {
    return [
      {
        field: 'id',
        headerName: 'id'
      },
      {
        field: 'logoLink',
        headerName: translator.logo(),
        width: 120,
        align: 'center',
        filterable: false,
        sortable: false,
        renderCell: (params) => {
          const url = params.row.logoLink as string;
          if (url) {
            return (
              <Avatar
                slotProps={{ img: { sx: { objectFit: 'contain' } } }}
                variant="square"
                src={url}
                sx={{ width: thumbSize, height: thumbSize }}
              />
            );
          }
        }
      },
      {
        field: 'name',
        headerName: translator.name(),
        flex: 1
      },
      {
        field: 'displayName',
        headerName: translator.displayName(),
        flex: 1
      },
      {
        field: 'deactivateAt',
        headerName: translator.deactivationDate(),
        flex: 1,
        renderCell: (params) => {
          const deactivateAt = params.row.deactivateAt as string;
          return fDate(deactivateAt);
        }
      },
      {
        ...activeColumnDefinition(translator)
      },
      {
        field: 'access',
        headerName: translator.accessColumnHeader(),
        width: 200,
        filterable: false,
        sortable: false,
        renderCell: (params) => (
          <AccessGuard rule={rules.childTenant.manageAccess}>
            <ChildTenantAccessCell childTenant={params.row} onManageAccess={setAccessChildTenant} />
          </AccessGuard>
        )
      },
      {
        ...editColumnDefinition((params) => {
          setOrganizationIdToEdit(params.row.id);
          setDisplayModal(true);
        }),
        renderCell: (cellValues) => (
          <AccessGuard rule={rules.childTenant.update}>
            <EditListItem
              onClick={() => {
                setOrganizationIdToEdit(cellValues.row.id);
                setDisplayModal(true);
              }}
            />
          </AccessGuard>
        )
      }
    ];
  };

  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const { childTenants, currentTenant, loading } = useSelector(
    (state: RootState) => state.organization
  );
  const { density } = useSelector((state: RootState) => state.datagrid);
  const canReadChildTenants = useAccess(rules.organization.manage);

  const allTenants = useMemo(() => {
    if (!canReadChildTenants) {
      return currentTenant ? [currentTenant] : [];
    }

    if (!currentTenant) {
      return childTenants;
    }

    const isAlreadyIncluded = childTenants.some((t) => t.id === currentTenant.id);
    return isAlreadyIncluded ? childTenants : [currentTenant, ...childTenants];
  }, [childTenants, currentTenant, canReadChildTenants]);
  const DATA_GRID_KEY = DataGridKey.OrganizationsList;
  const [displayModal, setDisplayModal] = useState(false);
  const [organizationIdToEdit, setOrganizationIdToEdit] = useState<string | undefined>();
  const [accessChildTenant, setAccessChildTenant] = useState<ChildTenant | null>(null);

  useEffect(() => {
    customDispatch({ action: getCurrentTenant, disableAllMessages: true });
    if (canReadChildTenants) {
      customDispatch({ action: getChildTenants, disableSuccessMessage: true });
      customDispatch({ action: getOrganizations, disableSuccessMessage: true });
    }
  }, [customDispatch, canReadChildTenants]);

  useEffect(() => {
    if (density[DATA_GRID_KEY] === 'compact') {
      thumbSize = 30;
    } else if (density[DATA_GRID_KEY] === 'standard') {
      thumbSize = 46;
    } else if (density[DATA_GRID_KEY] === 'comfortable') {
      thumbSize = 62;
    }
  }, [density, DATA_GRID_KEY]);

  return (
    <>
      <CustomDataGrid
        dataGridKey={DATA_GRID_KEY}
        columns={columns(translator)}
        rows={allTenants}
        getRowId={(org) => org.id}
        loading={loading.childTenants}
        checkboxSelection={false}
        pagination
      />
      <ChildTenantAccessPanel
        childTenant={accessChildTenant}
        onClose={() => setAccessChildTenant(null)}
      />
      <EditOrganizationDialog
        isOpen={displayModal}
        close={() => {
          setOrganizationIdToEdit(undefined);
          setDisplayModal(false);
        }}
        organizationIdToEdit={organizationIdToEdit}
        onSuccess={() => {
          customDispatch({ action: getChildTenants, disableSuccessMessage: true });
          customDispatch({ action: getOrganizationProfile, disableSuccessMessage: true });
          setDisplayModal(false);
        }}
      />
    </>
  );
};

export default Organizations;
